import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, useAuth } from "@/App";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useLocale } from "@/i18n/LocaleContext";
import { PartyPopper, MapPin, CalendarDays, Coins, Users, CheckCircle2, Loader2 } from "lucide-react";

function EventCard({ ev, busy, onJoinCredits, onJoinPaypal }) {
  const { t, lang } = useLocale();
  const [imgErr, setImgErr] = useState(false);
  const soldOut = ev.spots_left <= 0;
  const isFree = ev.credit_price === 0;
  const when = new Date(ev.date_time).toLocaleString(lang, { dateStyle: "medium", timeStyle: "short" });

  return (
    <div className="glass-card overflow-hidden border border-white/10" data-testid={`event-card-${ev.event_id}`}>
      {ev.photo_url && !imgErr ? (
        <img src={ev.photo_url} alt={ev.title} className="w-full aspect-video object-cover" onError={() => setImgErr(true)} />
      ) : (
        <div className="w-full aspect-[3/1] bg-gradient-to-r from-rose-600/25 via-fuchsia-600/20 to-amber-500/25 flex items-center justify-center">
          <PartyPopper className="w-10 h-10 text-rose-400/70" />
        </div>
      )}
      <div className="p-5 md:p-6">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h2 className="text-xl font-bold font-['Outfit'] tracking-tight">{ev.title}</h2>
          {ev.joined ? (
            <span className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-500/15 text-green-400 border border-green-500/40" data-testid={`event-joined-chip-${ev.event_id}`}>
              <CheckCircle2 className="w-3.5 h-3.5" /> {t("events.joinedChip")}
            </span>
          ) : soldOut ? (
            <span className="shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-500/15 text-red-400 border border-red-500/40">
              {t("events.soldOut")}
            </span>
          ) : (
            <span className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-white/5 text-muted-foreground border border-white/10">
              <Users className="w-3.5 h-3.5" /> {t("events.spotsLeft").replace("{n}", ev.spots_left)}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground mb-3">
          <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-primary" /> {when}</span>
          <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-primary" /> {ev.location}</span>
        </div>
        {ev.description && <p className="text-sm text-muted-foreground leading-relaxed mb-4 whitespace-pre-wrap">{ev.description}</p>}

        {!ev.joined && !soldOut && (
          <div className="mt-2">
            {!isFree && (
              <p className="text-xs text-muted-foreground mb-2">
                {t("events.feeNote").replace("{price}", ev.price.toFixed(2))} = <span className="text-white font-semibold">${ev.total_price.toFixed(2)}</span>
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-2.5">
              {isFree ? (
                <Button
                  className="flex-1 h-11 rounded-xl bg-primary hover:bg-primary/90 font-semibold"
                  disabled={!!busy}
                  onClick={() => onJoinCredits(ev)}
                  data-testid={`event-join-free-${ev.event_id}`}
                >
                  {busy === ev.event_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t("events.joinFree")}</>}
                </Button>
              ) : (
                <>
                  <Button
                    className="flex-1 h-11 rounded-xl bg-secondary/15 border border-secondary/40 text-secondary hover:bg-secondary/25 font-semibold"
                    variant="outline"
                    disabled={!!busy}
                    onClick={() => onJoinCredits(ev)}
                    data-testid={`event-join-credits-${ev.event_id}`}
                  >
                    {busy === ev.event_id ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                      <><Coins className="w-4 h-4 mr-2" /> {t("events.payCredits").replace("{n}", ev.credit_price)}</>
                    )}
                  </Button>
                  <Button
                    className="flex-1 h-11 rounded-xl bg-[#0070ba] hover:bg-[#005c99] text-white font-semibold"
                    disabled={!!busy}
                    onClick={() => onJoinPaypal(ev)}
                    data-testid={`event-join-paypal-${ev.event_id}`}
                  >
                    {busy === ev.event_id ? <Loader2 className="w-4 h-4 animate-spin" /> : t("events.payPaypal").replace("{total}", ev.total_price.toFixed(2))}
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Events() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const { t } = useLocale();
  const [events, setEvents] = useState(null);
  const [busy, setBusy] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const captureHandled = useRef(false);

  const fetchEvents = useCallback(() => {
    api.get("/events").then((r) => setEvents(r.data.events)).catch(() => setEvents([]));
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    if (captureHandled.current) return;
    const token = searchParams.get("token");
    const cancelled = searchParams.get("cancelled");
    if (!token && !cancelled) return;
    captureHandled.current = true;
    if (cancelled) {
      toast.info(t("events.payCancelled"));
      setSearchParams({}, { replace: true });
      return;
    }
    setBusy("capture");
    api.post(`/events/paypal-capture/${token}`)
      .then(() => {
        toast.success(t("events.joinedToast"));
        fetchEvents();
      })
      .catch((e) => toast.error(e.response?.data?.detail || t("events.captureFail")))
      .finally(() => {
        setBusy(null);
        setSearchParams({}, { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const onJoinCredits = async (ev) => {
    if (ev.credit_price > 0 && (user?.credits || 0) < ev.credit_price) {
      toast.error(t("events.notEnough").replace("{n}", ev.credit_price));
      navigate("/credits");
      return;
    }
    setBusy(ev.event_id);
    try {
      await api.post(`/events/${ev.event_id}/join-credits`);
      toast.success(t("events.joinedToast"));
      fetchEvents();
      checkAuth();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("events.captureFail"));
    } finally {
      setBusy(null);
    }
  };

  const onJoinPaypal = async (ev) => {
    setBusy(ev.event_id);
    try {
      const r = await api.post(`/events/${ev.event_id}/paypal-order`, {
        return_url: `${window.location.origin}/events`,
      });
      window.location.href = r.data.approve_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || t("events.captureFail"));
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="max-w-2xl mx-auto px-6 pt-24 pb-28" data-testid="events-page">
        <div className="flex items-center gap-3 mb-2">
          <PartyPopper className="w-8 h-8 text-rose-400" />
          <h1 className="text-4xl font-bold font-['Outfit'] tracking-tight">{t("events.title")}</h1>
        </div>
        <p className="text-muted-foreground mb-10">{t("events.subtitle")}</p>

        {busy === "capture" && (
          <div className="glass-card p-6 mb-6 flex items-center gap-3 border border-amber-500/40" data-testid="event-capturing">
            <Loader2 className="w-5 h-5 animate-spin text-amber-400" />
            <p className="text-sm">{t("events.loading")}</p>
          </div>
        )}

        {events === null && <p className="text-muted-foreground">{t("events.loading")}</p>}

        {events?.length === 0 && (
          <div className="text-center py-16 text-muted-foreground" data-testid="events-empty">
            <PartyPopper className="w-12 h-12 mx-auto mb-4 opacity-40" />
            <p>{t("events.empty")}</p>
          </div>
        )}

        <div className="space-y-6">
          {events?.map((ev) => (
            <EventCard key={ev.event_id} ev={ev} busy={busy} onJoinCredits={onJoinCredits} onJoinPaypal={onJoinPaypal} />
          ))}
        </div>
      </main>
    </div>
  );
}
